/**
 * Client-side service for JOC Club admin management.
 *
 * Calls the server-side admin API routes (`/api/admin/admins`, `/api/admin/me`),
 * which hold the KV credentials and enforce authorization. The browser never
 * talks to KV directly.
 */

export interface AdminRecord {
  address: string;
  addedBy: string;
  addedAt: string; // ISO string
}

export interface AdminStatus {
  ethAddress: string | null;
  isAdmin: boolean;
  isOwner: boolean;
}

export class AdminRequestError extends Error {
  status: number;
  body: string;

  constructor(status: number, body: string) {
    super(`Request failed (${status}): ${body}`);
    this.name = 'AdminRequestError';
    this.status = status;
    this.body = body;
    Object.setPrototypeOf(this, AdminRequestError.prototype);
  }
}

async function request<T>(url: string, init?: RequestInit): Promise<T> {
  const res = await fetch(url, {
    headers: { 'Content-Type': 'application/json' },
    ...init,
  });
  if (!res.ok) {
    const text = await res.text().catch(() => '');
    throw new AdminRequestError(res.status, text);
  }
  return res.json();
}

export async function getAdminStatus(): Promise<AdminStatus> {
  return request<AdminStatus>('/api/admin/me');
}

export async function getAdmins(): Promise<AdminRecord[]> {
  return request<AdminRecord[]>('/api/admin/admins');
}

export async function addAdmin(address: string): Promise<AdminRecord> {
  return request<AdminRecord>('/api/admin/admins', {
    method: 'POST',
    body: JSON.stringify({ address }),
  });
}

export async function removeAdmin(address: string): Promise<void> {
  await request<{ ok: boolean }>(`/api/admin/admins/${address}`, {
    method: 'DELETE',
  });
}
